// @flow

import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { commit } from '../mutations/UpdateProjectMutation';
import { postProjectToFA } from '../faPoster';

import styles from './styles/UpdateName.scss';

import type { Environment } from 'react-relay';
import type { IntlShape } from 'react-intl';
import type { FormStatus, Project } from '../types.flow';

const messages = defineMessages({
    nameCollisionError: {
        id: 'dc.ProjectForms.UpdateName.nameInputError',
        defaultMessage: 'You aleady have a project called "{projectName}"',
    },
    namePlaceholder: {
        id: 'dc.ProjectForms.UpdateName.namePlaceholder',
        defaultMessage: 'Example Project',
    },
});

export type UpdateNameProps = {|
    firmId: ?string,
    onSuccess?: (payload: Project) => mixed,
    project: Project,
    relayEnvironment: Environment,
    source: string,
    userId: string,
|};

type UpdateNameComponentProps = {|
    ...UpdateNameProps,
    intl: IntlShape,
|};

type UpdateNameState = {
    formStatus: FormStatus,
    nameCollisions: Array<string>,
    projectName: string,
};

export class UpdateName extends React.Component<UpdateNameComponentProps, UpdateNameState> {
    constructor(props: UpdateNameComponentProps) {
        super(props);
        this.handleMutationError = this.handleMutationError.bind(this);
        this.handleMutationSuccess = this.handleMutationSuccess.bind(this);
        this.hasInputNameCollision = this.hasInputNameCollision.bind(this);
        this.onNameUpdate = this.onNameUpdate.bind(this);
        this.submit = this.submit.bind(this);

        this.state = {
            formStatus: 'DEFAULT',
            nameCollisions: [],
            projectName: get(props, 'project.name'),
        };
    }

    handleMutationError: (error: mixed) => void;
    handleMutationSuccess: (payload: Project) => void;
    hasInputNameCollision: () => boolean;
    onNameUpdate: (e: SyntheticInputEvent<*>) => void;
    submit: (e: SyntheticInputEvent<*>) => void;

    handleMutationError(error: mixed) {
        const { nameCollisions, projectName } = this.state;
        const errors = get(error, 'source.errors') || [];
        const isProjectNameCollision = !!errors.find(err => get(err, 'requestInfo.status') === 409);

        if (isProjectNameCollision) {
            this.setState({
                nameCollisions: [...nameCollisions, projectName],
                formStatus: 'DEFAULT',
            });
        } else {
            this.setState({ formStatus: 'ERROR' });
        }
    }

    handleMutationSuccess(payload: Project) {
        const { firmId, onSuccess, source, userId } = this.props;
        const project = get(payload, 'updateProject.project');

        postProjectToFA({
            action: 'UPDATE',
            firmId,
            project,
            source,
            userId,
        });

        if (typeof onSuccess === 'function') {
            onSuccess(payload);
        }
    }

    hasInputNameCollision() {
        const { projectName, nameCollisions } = this.state;
        return !!projectName && nameCollisions.indexOf(projectName) > -1;
    }

    onNameUpdate(e: SyntheticInputEvent<*>) {
        this.setState({
            projectName: e.target.value,
        });
    }

    submit(e: SyntheticInputEvent<*>) {
        e.preventDefault();

        const { formStatus, projectName } = this.state;
        const { project, relayEnvironment } = this.props;

        if (formStatus === 'LOADING') {
            return;
        }

        this.setState({ formStatus: 'LOADING' });

        commit(relayEnvironment, {
            projectId: project.serviceId,
            name: projectName,
            onCompleted: this.handleMutationSuccess,
            onError: this.handleMutationError,
        });
    }

    render() {
        const { intl } = this.props;
        const { formStatus, projectName } = this.state;
        const hasInputNameCollision = this.hasInputNameCollision();
        const canSubmit = !!projectName && !hasInputNameCollision;
        const nameInputError = hasInputNameCollision
            ? intl.formatMessage(messages.nameCollisionError, { projectName })
            : '';

        return (
            <form onSubmit={this.submit}>
                <div className={styles.formSection}>
                    <div className={styles.formSectionPrompt}>
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateName.name"
                            defaultMessage="Project Name"
                        />
                    </div>
                    <Input
                        dataTn="project-name-input"
                        errorMessage={nameInputError}
                        onChange={this.onNameUpdate}
                        maxLength={31}
                        placeholder={intl.formatMessage(messages.namePlaceholder)}
                        value={projectName}
                    />
                </div>
                <Button
                    className={styles.submitButton}
                    dataTn="update-project-name-submit-button"
                    disabled={!canSubmit}
                >
                    {formStatus === 'LOADING' ? (
                        <Spinner containerClass={styles.spinnerContainer} />
                    ) : (
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateName.submitCta"
                            defaultMessage="Done"
                        />
                    )}
                </Button>
                {formStatus === 'ERROR' && (
                    <div className={styles.errorMessage}>
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateName.errorMessage"
                            defaultMessage="An error occurred. Project name could not be updated."
                        />
                    </div>
                )}
            </form>
        );
    }
}

export default injectIntl(UpdateName);
